<template>
  <div class="mb-2">
    <b-row>
      <b-col lg="6">
        <b-form-group label="Set Lead Time" label-class="fulfillment-form-label"></b-form-group>
        <b-form-group label="Minimum">
          <b-row>
            <b-col lg="6" class="mb-3 mb-lg-0">
              <b-form-select
                :class="{
                  'text-muted': !form.minimum,
                  'border-danger': isInvalid(`${origin}.minimum`)
                }"
                v-model="form.minimum"
                :options="computedTypeOptions('minimum')"
                :disabled="!enabled"
                @change="emitLeadTimeValues">
              </b-form-select>
              <b-form-invalid-feedback :force-show="isInvalid(`${origin}.minimum`)">
                {{ errorMessage(`${origin}.minimum`) }}
              </b-form-invalid-feedback>
            </b-col>
            <b-col lg="6">
              <FulfillmentInputDropdown
                :items="computedValueOptions('minimum')"
                :enabled="!form.minimum || !enabled"
                origin="minimum"
                @setValue="handleSetValue"
                :selectedValue="form.minimum_lead_time"
                :type="form.minimum"
                :error-key="`${origin}.minimum_lead_time`"
                :parent="origin"
              />
            </b-col>
          </b-row>
        </b-form-group>
        <b-form-group label="Duration">
          <b-row>
            <b-col lg="6" class="mb-3 mb-lg-0">
              <b-form-select
                :class="{
                  'text-muted': !form.duration,
                  'border-danger': isInvalid(`${origin}.duration`)
                }"
                v-model="form.duration"
                :options="computedTypeOptions('duration')"
                :disabled="!enabled"
                @change="emitLeadTimeValues">
              </b-form-select>
              <b-form-invalid-feedback :force-show="isInvalid(`${origin}.duration`)">
                {{ errorMessage(`${origin}.duration`) }}
              </b-form-invalid-feedback>
            </b-col>
            <b-col lg="6">
              <FulfillmentInputDropdown
                :items="computedValueOptions('duration')"
                :enabled="!form.duration || !enabled"
                origin="duration"
                @setValue="handleSetValue"
                :selectedValue="form.duration_lead_time"
                :type="form.duration"
                :error-key="`${origin}.duration_lead_time`"
                :parent="origin"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FulfillmentInputDropdown from './FulfillmentInputDropdown';

function initForm() {
  return {
    minimum: null,
    minimum_lead_time: null,
    duration: null,
    duration_lead_time: null
  }
};

export default {
  name: "LeadTimeComponent",
  props: ["enabled", "origin", "selectedValue"],
  components: { FulfillmentInputDropdown },
  data() {
    return {
      form: initForm(),
      typeOptions: [
        { value: null, text: 'Select', disabled: true },
        { value: 'minutes', text: 'Minutes' },
        { value: 'hours', text: 'Hour' },
        { value: 'days', text: 'Day' },
        { value: 'weeks', text: 'Week' },
        { value: 'months', text: 'Month' },
        { value: 'years', text: 'Year' }
      ],
      storedForm: null
    }
  },
  computed: {
    ...mapGetters({
      errors: 'orderFulfillment/errors',
      delivery_tab_settings: 'orderFulfillment/delivery_tab_settings',
      pickup_tab_settings: 'orderFulfillment/pickup_tab_settings'
    })
  },
  methods: {
    computedTypeOptions(type) {
      switch(type) {
        case 'minimum':
          const available_minimum = [null, 'minutes', 'hours', 'days', 'months'];
          return available_minimum.map(avl => {
            return this.typeOptions.find(opt => opt.value === avl);
          });
        case 'duration':
          const available_duration = [null, 'days', 'weeks', 'months', 'years'];
          return available_duration.map(avl => {
            return this.typeOptions.find(opt => opt.value === avl);
          });
        default:
          return [];
      }
    },
    computedValueOptions(type) {
      const values = { // declared values per type
        minutes: [{ value: 30, text: 30 }, { value: 60, text: '60 (1 hr)' }, { value: 90, text: '90 (1.5hr)' }],
        hours: 24,
        days: 30,
        weeks: 32,
        months: 12,
        years: 5
      };

      const generateValues = (length) => { // generate values based on selected type
        return Array.from({ length: length }, (v, k) => k+1)
      };

      switch(type) {
        case 'minimum':
          const minimum = this.form.minimum;
          return minimum === 'minutes'
            ? values[minimum]
            : generateValues(values[minimum]);
        case 'duration':
          const duration = this.form.duration;
          return generateValues(values[duration]);
        default:
          return [];
      }
    },
    handleSetValue(data) { // handle emitted value from Fulfillment Dropdown Component
      this.form[`${data.type}_lead_time`] = data.value;
      this.$nextTick(() => {
        this.emitLeadTimeValues();
      });
    },
    emitLeadTimeValues() {
      this.$emit('setLeadTimeValues', this.form);
      if (this.delivery_tab_settings && this.origin === 'scheduled_delivery') {
        const stored_lead_time = this.delivery_tab_settings.pre_order_schedule_settings.lead_time;
        if (this.form.minimum !== stored_lead_time.minimum_by || this.form.duration !== stored_lead_time.duration_by) this.$root.$emit('changesDetected');
      } else if (this.pickup_tab_settings && this.origin === 'scheduled_pickup') {
        const stored_lead_time = this.pickup_tab_settings.pre_order_schedule_settings.lead_time;
        if (this.form.minimum !== stored_lead_time.minimum_by || this.form.duration !== stored_lead_time.duration_by) this.$root.$emit('changesDetected');
      }
    },
    isInvalid(key) {
      return this.errors && this.errors[key]
        ? true
        : false;
    },
    errorMessage(key) {
      return this.errors && this.errors[key]
        ? this.errors[key].split('.').join(' ')
        : null;
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        // reset value to null if value options (based on type) doesn't include previously selected value
        const types = ['minimum', 'duration'];
        const isMinimumMinutes = this.form.minimum === 'minutes';

        types.forEach(type => {
          const valueOptions = type === 'minimum' && isMinimumMinutes
            ? this.computedValueOptions(type).map(opt => opt.value)
            : this.computedValueOptions(type);
          if (!valueOptions.includes(this.form[`${type}_lead_time`])) this.form[`${type}_lead_time`] = null;
        });
      }
    },
    selectedValue(lead_time_values) {
      this.form = lead_time_values;
    }
  }
};
</script>
